import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { NotificationService } from '../../api/services/notification.service';
import { Notification } from '../../api/models';
import { HeaderNavComponent } from 'src/app/ui/header-nav/header-nav.component';
import { FooterComponent } from 'src/app/ui/footer/footer.component';

/** "Just now", "5 min ago", "3 h ago", "Yesterday, 22:10", else the date. */
export function relativeTime(at: Date | null, now: Date = new Date()): string {
  if (!at) return '';
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60000);
  // Negative when the server's clock is slightly ahead of the browser's.
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const sameDay = at.toDateString() === now.toDateString();
  if (minutes < 24 * 60 && sameDay) return `${Math.floor(minutes / 60)} h ago`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const time = new DatePipe('en-GB').transform(at, 'HH:mm');
  if (at >= yesterday) return `Yesterday, ${time}`;
  return new DatePipe('en-GB').transform(at, 'd MMM, HH:mm') ?? '';
}

/**
 * What the bar has been told: orders placed, payments taken, stock running out.
 *
 * Same behaviour as the mobile app's screen. Unread items carry an accent bar,
 * a tint, a bold title and a dot; each type has its own icon and colour;
 * opening one shows it in full and marks it read.
 */
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatButtonToggleModule,
    MatSnackBarModule, MatProgressSpinnerModule, HeaderNavComponent, FooterComponent,
  ],
  templateUrl: './notifications.component.html',
})
export class NotificationsComponent implements OnInit {
  private static readonly PAGE_SIZE = 20;

  isLoading = false;
  loadingMore = false;
  hasMore = true;
  error: string | null = null;
  notifications: Notification[] = [];
  /** The server's total, not a count of what is loaded (which caps at a page). */
  unreadCount = 0;
  filter: 'all' | 'unread' = 'all';
  selected: Notification | null = null;
  private page = 0;

  readonly relativeTime = relativeTime;

  constructor(
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get visible(): Notification[] {
    return this.filter === 'unread' ? this.notifications.filter(n => !n.isRead) : this.notifications;
  }

  load(): void {
    this.isLoading = true;
    this.error = null;
    forkJoin({
      items: this.notificationService.getNotifications(0, NotificationsComponent.PAGE_SIZE),
      unread: this.notificationService.getUnreadCount(),
    }).subscribe({
      next: ({ items, unread }) => {
        this.notifications = items;
        this.unreadCount = unread;
        this.page = 0;
        this.hasMore = items.length === NotificationsComponent.PAGE_SIZE;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.error = 'Could not load your notifications. Please try again.';
      },
    });
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    this.notificationService.getNotifications(this.page + 1, NotificationsComponent.PAGE_SIZE).subscribe({
      next: next => {
        const known = new Set(this.notifications.map(n => n.id));
        this.notifications = [...this.notifications, ...next.filter(n => !known.has(n.id))];
        this.page += 1;
        this.hasMore = next.length === NotificationsComponent.PAGE_SIZE;
        this.loadingMore = false;
      },
      error: () => { this.loadingMore = false; },
    });
  }

  /** Opening a notification reads it: the list updates at once, the server follows. */
  open(n: Notification): void {
    this.selected = n;
    window.scrollTo({ top: 0 });
    if (n.isRead) return;
    n.isRead = true;
    this.unreadCount = Math.max(0, this.unreadCount - 1);
    this.notificationService.markAsRead(n.id).subscribe({
      error: () => {
        n.isRead = false;
        this.unreadCount += 1;
        this.snackBar.open('Could not mark as read', 'Close', { duration: 3000 });
      },
    });
  }

  closeDetail(): void {
    this.selected = null;
  }

  markAllAsRead(): void {
    const unreadBefore = this.notifications.filter(n => !n.isRead);
    const countBefore = this.unreadCount;
    unreadBefore.forEach(n => n.isRead = true);
    this.unreadCount = 0;
    this.notificationService.markAllAsRead().subscribe({
      next: () => this.snackBar.open('All notifications marked as read', 'Close', { duration: 3000 }),
      error: () => {
        unreadBefore.forEach(n => n.isRead = false);
        this.unreadCount = countBefore;
        this.snackBar.open('Could not mark all as read', 'Close', { duration: 3000 });
      },
    });
  }

  tint(n: Notification): string {
    // 7% of the kind colour over white, as on mobile.
    return n.isRead ? '#ffffff' : n.kind.color + '12';
  }

  onBack(): void {
    if (this.selected) { this.closeDetail(); return; }
    this.router.navigate(['/']);
  }
}
